import * as React from 'react';

import {
	ErrorMessage,
	FieldProps,
	Field,
	FormikProps,
} from 'formik';

import { BaseParams } from '@common/typescript/objects/BaseParams';
import { WithDeleted } from '@common/typescript/objects/WithDeleted';
import { defaultErrorComponent } from '@common/react/components/Forms/FormikInput/FormikInput';
import { EditableList, EditableListProps } from '@common/react/components/Core/EditableList/EditableList';

interface FormikListProps<
	T extends WithDeleted,
	TFormValues = BaseParams
> extends Omit<EditableListProps<T>, 'items' | 'setItems' | 'renderInput'> {
	fieldName: string;
	renderInput: (addNewItem: (item: T) => void, form: FormikProps<TFormValues>) => JSX.Element;
	showValidateAfterSubmit?: boolean;
	ErrorComponent?: React.FC<{error: string}>;
	className?: string;
	label?: string;
}

export interface BaseFormikFieldListProps<T extends WithDeleted, TFormValues = BaseParams, TFilters = BaseParams> {
	formikListProps?: FormikListProps<T, TFormValues>;
	objectType: string;
	objectId: number;
	filters?: TFilters | ((form: FormikProps<TFormValues>) => TFilters);
	fieldName?: string;
	label?: string;
	withLink?: boolean;
	className?: string;
	requestType?: string;
}

export const FormikList = <T extends WithDeleted, TFormValues = BaseParams>(
	props: FormikListProps<T, TFormValues>,
) => {
	const {
		showValidateAfterSubmit,
		ErrorComponent = defaultErrorComponent,
		fieldName,
		className,
		label = '',
		renderInput,
		...rest
	} = props;

	return <div className={className}>
		{label && <label htmlFor={fieldName}>{label}</label>}
		<Field name={fieldName}>
			{({ field, form }: FieldProps) => {
				let arr = (field.value || []) as Array<T>;

				if (!Array.isArray(arr)) {
					arr = field.value.list;
				}

				return <EditableList<T>
					items={arr}
					setItems={(items) => form.setFieldValue(field.name, items)}
					renderInput={(addNewItem) => {
						return <>
							{showValidateAfterSubmit && form.submitCount > 0
								&& <ErrorMessage name={field.name} render={(msg) => <ErrorComponent error={msg} />} />}
							{renderInput(addNewItem, form)}
						</>;
					}}
					{...rest}
				/>;
			}}
		</Field>
	</div>;
};
